"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
// import { StartupSvg, InvestorSvg, AdvisorSvg } from "@/lib/svg";
import { useState } from "react";
// import { IconCircleDashedCheck } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import useWaitList from "@/hooks/useWaitList";
import { User } from "@/types/waitlist/types";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { v4 as uuidv4 } from "uuid";
import { IndustriesList } from "@/lib/data";

const fundingStageOptions = [
  "Currently Raising Pre-seed",
  "Pre-Seed",
  "Seed",
  "Series A",
  "Series B",
  "Series C",
];

const serviceProviders = [
  "Google Cloud",
  "AWS",
  "Azure",
  "Vercel",
  "GCP",
  "Digital Ocean",
  "Linode",
];
const formSchema = z.object({
  mobileNumber: z.string().min(5, "Please enter a valid mobile number"),
  linkedinUrl: z.string().min(2, "Linkedin URL is required"),
  websiteUrl: z.string().optional(),
  isPitching: z.boolean(),
  companyName: z.string().optional(),
  fundingStage: z.string().min(2, "This field is required"),
  fundingRaised: z.string().optional(),
  fundingRequired: z.string().optional(),
  industry: z.array(z.string()),
  pitchDeck: z.string().optional(),
  serviceProviders: z.array(z.string()).optional(),
});

export default function FounderForm({ user }: { user: User }) {
  const { saveFounderForm } = useWaitList();
  const [error, setError] = useState<boolean>(false);
  const router = useRouter();
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      mobileNumber: "",
      linkedinUrl: "",
      websiteUrl: "",
      isPitching: false,
      fundingStage: "",
      fundingRaised: "",
      fundingRequired: "",
      industry: [],
      serviceProviders: [],
    },
  });

  const { mutateAsync: saveFounderDetails, isPending } = useMutation({
    mutationFn: saveFounderForm,
    onSuccess: (_response) => {
      router.push("/success");
    },
    onError: (response) => {
      console.log("error", response);
      setError(true);
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    await saveFounderDetails({
      ...values,
      id: uuidv4(),
      email: user?.email || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      role: user?.role || "",
      websiteUrl: values?.websiteUrl || "",
      companyName: values?.companyName || "",
      fundingRaised: values?.fundingRaised || "",
      fundingRequired: values?.fundingRequired || "",
      pitchDeck: values?.pitchDeck || "",
      serviceProviders: values?.serviceProviders || [],
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted p-6">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <h1 className="mb-4 text-4xl font-bold  bg-clip-text text-transparent bg-gradient-to-b from-neutral-200 to-neutral-600 tracking-tight">
            Welcome to the global startup community
          </h1>
          <p className="text-lg text-muted-foreground">
            Get early access offers and discounts. Sign up now to secure your
            spot.
          </p>
        </div>

        <div className="rounded-xl border bg-card p-6 shadow-lg">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="mobileNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Mobile Number <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="+44 7234567890" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="linkedinUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Linkedin URL <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://www.linkedin.com/in/john-doe"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isPitching"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>Are you a founder looking to pitch?</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="fundingStage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Which funding round are you looking to raise?
                      <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        // defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a funding stage" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {fundingStageOptions.map((stage) => (
                            <SelectItem key={stage} value={stage}>
                              {stage}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      What Industry does your company operate in?{" "}
                      <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <MultiSelect
                        className="bg-white dark:bg-input/50"
                        options={IndustriesList.map((provider) => ({
                          label: provider,
                          value: provider,
                        }))}
                        onValueChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What is the name of your company?</FormLabel>
                    <FormControl>
                      <Input placeholder="Syrena" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="websiteUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://www.example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="fundingRaised"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      How much have you successfully raised (£)?
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="fundingRequired"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      How much are you currently looking to raise (£)?
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pitchDeck"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Share a link to your pitch deck</FormLabel>
                    <FormControl>
                      <Input placeholder="https://www.example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serviceProviders"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Current Cloud Providers(Select All that Apply)
                    </FormLabel>
                    <FormControl>
                      <MultiSelect
                        className="bg-white dark:bg-input/50"
                        options={serviceProviders.map((provider) => ({
                          label: provider,
                          value: provider,
                        }))}
                        onValueChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                disabled={isPending}
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-300 font-semibold cursor-pointer"
              >
                {isPending ? "Submitting..." : "Submit"}
              </Button>
              {error && (
                <div className="w-full flex flex-col items-center font-semibold">
                  <div className="text-red-500">
                    You are already on the Wait List!
                  </div>
                  <div className="text-muted-foreground">
                    We will reach out to you soon :)
                  </div>
                </div>
              )}
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
